import { useCallback, useEffect, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getProfile, isApiConfigured } from './api';
import { Profile } from '../types/content';

export interface ProfileState {
  profile: Profile | null;
  loading: boolean;
  /** Re-reads the profile, e.g. after the user edits their details. */
  refresh: () => void;
}

/**
 * The signed-in user's profile, or null when signed out.
 *
 * Used by the navbar to decide whether to show the Admin link — role lives in
 * MongoDB, not in the token, so it has to be asked for. Signed-out visitors
 * make no network call: `getCurrentUser()` fails locally first.
 */
export function useProfile(): ProfileState {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        await getCurrentUser();
      } catch {
        if (!cancelled) {
          setProfile(null);
          setLoading(false);
        }
        return;
      }

      if (!isApiConfigured) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const me = await getProfile();
        if (!cancelled) setProfile(me);
      } catch {
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    // Signing in or out must flip the navbar without a page reload.
    const stop = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') void load();
      if (payload.event === 'signedOut') setProfile(null);
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [nonce]);

  return { profile, loading, refresh };
}
